import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { parseCookies } from "nookies";
import {
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Toast,
  ToastContainer,
  Table,
  Modal,
  Dropdown,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import {
  IconMessage,
  IconSend,
  IconUsers,
  IconClock,
  IconRefresh,
  IconPlus,
  IconEye,
  IconUserCheck,
  IconX,
  IconStar,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";
import { isClientPlan } from "../../../utils/PlanUtils";
import { 
  getChats,
  createChat,
  assignChat,
  closeChat,
  getChatMessages,
  sendChatMessage,
  createChatWebSocket,
  sendWebSocketMessage,
  getAvailableAgents
} from "../../../services/SupportServices";

const SupportChat = ({ config, isClientView = false }) => {
  const { user } = useContext(UserContext);
  
  // Estados principais
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  
  // Estados de WebSocket
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  
  // Estados de UI
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [chatToClose, setChatToClose] = useState(null);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");
  
  // Estados para novo chat
  const [newChatForm, setNewChatForm] = useState({
    subject: "",
    priority: "medium"
  });
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.id);
      let room_id = selectedChat?.room_id || selectedChat?.id;
      connectToChat(room_id);
    }
    
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [selectedChat]);

  const showNotification = (message, type = "info") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, scrollToBottom]);

  // ==========================================
  // FUNÇÕES DE API
  // ==========================================

  const loadChats = async () => {
    try {
      setIsLoadingChats(true);
      
      const filters = {};
      // Se for visualização de cliente, filtrar apenas chats do próprio usuário
      if (isClientView) {
        filters.customer_id = user?.id;
        console.log("🔄 Carregando chats para cliente:", user?.id);
      } else {
        console.log("🔄 Carregando todos os chats (visão administrativa)");
      }
      
      const result = await getChats(filters);
      
      if (result.success) {
        console.log("✅ Chats carregados:", result.data?.length || 0, "chats");
        setChats(result.data || []);
      } else if (result.isApiUnavailable) {
        console.log("⚠️ API não está disponível, usando chats vazios");
        setChats([]);
      } else {
        console.log("❌ Erro ao carregar chats:", result.error);
        setChats([]);
        showNotification("Erro ao carregar lista de chats", "error");
      }
    } catch (error) {
      console.warn("Erro inesperado ao carregar chats:", error.message);
      setChats([]);
    } finally {
      setIsLoadingChats(false);
    }
  };

  const loadMessages = async (chatId) => {
    try {
      console.log("🔄 Carregando mensagens para chat:", chatId, "isClientView:", isClientView);
      const result = await getChatMessages(chatId);
      
      if (result.success) {
        console.log("✅ Mensagens carregadas:", result.data?.length || 0, "mensagens");
        setMessages(result.data || []);
      } else if (result.isApiUnavailable) {
        console.log("⚠️ API não está disponível, usando mensagens vazias");
        setMessages([]);
      } else {
        console.log("❌ Erro ao carregar mensagens:", result.error);
        setMessages([]);
        showNotification("Erro ao carregar mensagens do chat", "error");
      }
    } catch (error) {
      console.warn("Erro inesperado ao carregar mensagens:", error.message);
      setMessages([]);
    }
  };

  const createNewChat = async () => {
    try {
      setIsLoading(true);
      
      const result = await createChat(newChatForm);
      
      if (result.success) {
        setChats(prev => [result.data, ...prev]);
        setSelectedChat(result.data);
        setShowNewChatModal(false);
        setNewChatForm({ subject: "", priority: "medium" });
        showNotification("Chat criado com sucesso!", "success");
      } else if (result.isApiUnavailable) {
        showNotification("API de suporte ainda não está disponível", "error");
      } else {
        showNotification(result.error || "Erro ao criar novo chat", "error");
      }
    } catch (error) {
      console.warn("Erro inesperado ao criar chat:", error.message);
      showNotification("Erro inesperado ao criar chat", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const assignAgent = async (chatId, agentId = null) => {
    try {
      let finalAgentId = agentId;
      
      // Se não foi fornecido um agentId específico, buscar agente disponível
      if (!finalAgentId) {
        console.log("🔄 Buscando agente disponível para atribuir ao chat", chatId);
        const availableAgentsResult = await getAvailableAgents();
        
        if (availableAgentsResult.success && availableAgentsResult.data?.length > 0) {
          // Usar o primeiro agente disponível
          finalAgentId = availableAgentsResult.data[0].id;
          console.log("✅ Agente disponível encontrado:", finalAgentId);
        } else {
          // Se não há agentes disponíveis, para organizações (plan_id 2,5) usar o próprio usuário
          if (user?.plan_id && [2, 5, 9, 10, 11].includes(user.plan_id)) {
            finalAgentId = user.id;
            console.log("⚠️ Nenhum agente disponível, usando admin da organização como agente:", finalAgentId);
          } else {
            showNotification("Nenhum agente disponível para atender este chat", "warning");
            return;
          }
        }
      }
      
      const result = await assignChat(chatId, finalAgentId);
      
      if (result.success) {
        loadChats();
        showNotification("Agente atribuído com sucesso!", "success");
      } else if (result.isApiUnavailable) {
        showNotification("API de suporte ainda não está disponível", "error");
      } else {
        showNotification(result.error || "Erro ao atribuir agente", "error");
      }
    } catch (error) {
      console.warn("Erro inesperado ao atribuir agente:", error.message);
      showNotification("Erro inesperado ao atribuir agente", "error");
    }
  };

  const closeChatHandler = async () => {
    try {
      setIsLoading(true);
      
      const closeData = {};
      if (rating !== 5) closeData.rating = rating;
      if (feedback.trim()) closeData.feedback = feedback.trim();
      
      const result = await closeChat(chatToClose.id, closeData);
      
      if (result.success) {
        setChats(prev => prev.filter(chat => chat.id !== chatToClose.id));
        if (selectedChat && selectedChat.id === chatToClose.id) {
          setSelectedChat(null);
          setMessages([]);
        }
        setShowCloseModal(false);
        setChatToClose(null);
        setRating(5);
        setFeedback("");
        showNotification("Chat finalizado com sucesso!", "success");
      } else if (result.isApiUnavailable) {
        showNotification("API de suporte ainda não está disponível", "error");
      } else {
        showNotification(result.error || "Erro ao finalizar chat", "error");
      }
    } catch (error) {
      console.warn("Erro inesperado ao finalizar chat:", error.message);
      showNotification("Erro inesperado ao finalizar chat", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // WEBSOCKET
  // ==========================================

  const connectToChat = (roomId) => {
    if (socket) {
      socket.close();
    }

    // Obter token dos cookies como no NotificationsContext
    const { token: userToken } = parseCookies();
    const newSocket = createChatWebSocket(roomId, userToken, {
      onOpen: () => {
        setConnectionStatus("connected");
        setSocket(newSocket);
      },
      onMessage: (data) => {
        handleWebSocketMessage(data);
      },
      onClose: () => {
        setConnectionStatus("disconnected");
        setSocket(null);
      },
      onError: (error) => {
        setConnectionStatus("error");
        showNotification("Erro na conexão do chat", "error");
      }
    });
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'message':
        setMessages(prev => [...prev, data.message]);
        break;
      case 'typing':
        setIsTyping(data.is_typing);
        setTypingUser(data.is_typing ? data.user_name : null);
        break;
      case 'system':
        showNotification(data.message, "info");
        break;
      case 'error':
        showNotification(data.message, "error");
        break;
      default:
        console.log("Mensagem WebSocket desconhecida:", data);
    }
  };

  const sendWebSocketMessageHandler = (type, payload) => {
    return sendWebSocketMessage(socket, type, payload);
  };

  // ==========================================
  // ENVIO DE MENSAGENS
  // ==========================================

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !selectedChat) return;

    const messageText = inputMessage;
    setInputMessage("");
    setIsLoading(true);

    console.log("📤 Enviando mensagem:", messageText, "para chat:", selectedChat.id);

    try {
      // Enviar via WebSocket se conectado, senão via API
      if (sendWebSocketMessageHandler("message", { content: messageText })) {
        console.log("✅ Mensagem enviada via WebSocket");
      } else {
        console.log("🔄 Enviando mensagem via API REST");
        // Fallback para API REST
        const result = await sendChatMessage(selectedChat.id, {
          content: messageText,
          message_type: "text"
        });

        if (result.success) {
          console.log("✅ Mensagem enviada via API, recarregando mensagens");
          loadMessages(selectedChat.id);
        } else if (result.isApiUnavailable) {
          console.log("⚠️ API não disponível ao enviar mensagem");
          showNotification("API de suporte ainda não está disponível", "error");
          setInputMessage(messageText); // Restaurar mensagem
        } else {
          console.log("❌ Erro ao enviar mensagem:", result.error);
          showNotification(result.error || "Erro ao enviar mensagem", "error");
          setInputMessage(messageText); // Restaurar mensagem
        }
      }
    } catch (error) {
      console.warn("Erro inesperado ao enviar mensagem:", error.message);
      showNotification("Erro inesperado ao enviar mensagem", "error");
      setInputMessage(messageText); // Restaurar mensagem em caso de erro
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTyping = () => {
    if (sendWebSocketMessageHandler("typing", { is_typing: true })) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        sendWebSocketMessageHandler("typing", { is_typing: false });
      }, 1000);
    }
  };

  // ==========================================
  // RENDERIZAÇÃO
  // ==========================================

  const getPriorityBadge = (priority) => {
    const variants = {
      low: "success",
      medium: "warning", 
      high: "danger",
      urgent: "dark"
    };
    
    const labels = {
      low: "Baixa",
      medium: "Média",
      high: "Alta", 
      urgent: "Urgente"
    };
    
    return (
      <Badge bg={variants[priority] || "secondary"}>
        {labels[priority] || priority}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      waiting: "warning",
      active: "success",
      closed: "secondary"
    };
    
    const labels = {
      waiting: "Aguardando",
      active: "Ativo",
      closed: "Fechado"
    };
    
    return (
      <Badge bg={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  const renderMessage = (message) => {
    const isFromUser = message.is_from_agent === false;
    
    return (
      <div 
        key={message.id} 
        className={`d-flex mb-3 ${isFromUser ? 'justify-content-end' : 'justify-content-start'}`}
      >
        <div
          className={`p-3 rounded-3 position-relative ${
            isFromUser 
              ? 'bg-primary text-white' 
              : 'bg-success text-white'
          }`}
          style={{
            maxWidth: "70%",
            wordWrap: "break-word",
            borderRadius: isFromUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px"
          }}
        >
          <div className="mb-1">
            <small className="fw-semibold">
              {message.sender_name}
            </small>
          </div>
          
          <div>{message.content}</div>
          
          <div className={`mt-2 small text-${isFromUser ? 'end' : 'start'}`}>
            <span className={isFromUser ? "" : "text-white-50"}>
              {new Date(message.sent_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (!config || !config.is_active) {
    return (
      <Alert variant="warning">
        <h5>Sistema de Chat Inativo</h5>
        <p>Configure e ative o sistema de suporte para usar o chat ao vivo.</p>
      </Alert>
    );
  }

  if (config.support_type === "ticket") {
    return (
      <Alert variant="info">
        <h5>Chat Não Disponível</h5>
        <p>Sua configuração atual permite apenas suporte via tickets.</p>
      </Alert>
    );
  }

  return (
    <Row>
      <Col md={4}>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <IconMessage className="me-2" />
              {isClientView ? "Meus Chats" : "Chats Ativos"}
            </h6>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={loadChats}
                disabled={isLoadingChats}
              >
                <IconRefresh size={16} />
              </Button>
              {(isClientView || isClientPlan(user?.plan_id)) && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setShowNewChatModal(true)}
                >
                  <IconPlus size={16} />
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {isLoadingChats ? (
              <div className="text-center p-4">
                <Spinner animation="border" size="sm" />
                <p className="mt-2 mb-0 text-muted">Carregando chats...</p>
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center p-4">
                <IconMessage size={48} className="text-muted mb-3" />
                <p className="text-muted mb-0">
                  {isClientView ? "Você ainda não tem chats" : "Nenhum chat ativo"}
                </p>
                {isClientView && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => setShowNewChatModal(true)}
                  >
                    Iniciar Primeiro Chat
                  </Button>
                )}
              </div>
            ) : (
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                {chats.map(chat => (
                  <div
                    key={chat.id}
                    className={`p-3 border-bottom cursor-pointer hover-bg-light ${
                      selectedChat?.id === chat.id ? 'bg-light' : ''
                    }`}
                    onClick={() => setSelectedChat(chat)}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-1 text-truncate" style={{ maxWidth: "200px" }}>
                        {chat.subject}
                      </h6>
                      {getStatusBadge(chat.status)}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {chat.customer_name}
                      </small>
                      {getPriorityBadge(chat.priority)}
                    </div>
                    
                    <div className="mt-2">
                      <small className="text-muted">
                        <IconClock size={12} className="me-1" />
                        {new Date(chat.started_at).toLocaleString()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={8}>
        {!selectedChat ? (
          <Card>
            <Card.Body className="text-center py-5">
              <IconMessage size={64} className="text-muted mb-3" />
              <h5>{isClientView ? "Selecione um Chat" : "Selecione um Chat"}</h5>
              <p className="text-muted">
                {isClientView 
                  ? "Escolha um chat da lista ao lado ou crie um novo chat para começar"
                  : "Escolha um chat da lista ao lado para iniciar o atendimento"
                }
              </p>
              {isClientView && chats.length === 0 && (
                <Button
                  variant="primary"
                  onClick={() => setShowNewChatModal(true)}
                  className="mt-3"
                >
                  <IconPlus size={16} className="me-2" />
                  Criar Primeiro Chat
                </Button>
              )}
            </Card.Body>
          </Card>
        ) : (
          <Card style={{ height: "600px", display: "flex", flexDirection: "column" }}>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1">{selectedChat.subject}</h6>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">
                      Cliente: {selectedChat.customer_name}
                    </small>
                    {getStatusBadge(selectedChat.status)}
                    {getPriorityBadge(selectedChat.priority)}
                  </div>
                  <div className="mt-1">
                    <Badge 
                      bg={connectionStatus === "connected" ? "success" : "danger"}
                      className="me-2"
                    >
                      {connectionStatus === "connected" ? "Conectado" : "Desconectado"}
                    </Badge>
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  {/* Botões administrativos - apenas para planos avançados */}
                  {!isClientView && (
                    <>
                      {selectedChat.status === "waiting" && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => assignAgent(selectedChat.id)}
                        >
                          <IconUserCheck size={16} className="me-1" />
                          Atender
                        </Button>
                      )}
                      
                      {selectedChat.status === "active" && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            setChatToClose(selectedChat);
                            setShowCloseModal(true);
                          }}
                        >
                          <IconX size={16} className="me-1" />
                          Finalizar
                        </Button>
                      )}
                    </>
                  )}
                  
                  {/* Botão para cliente finalizar próprio chat */}
                  {isClientView && selectedChat.status === "active" && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setChatToClose(selectedChat);
                        setShowCloseModal(true);
                      }}
                    >
                      <IconX size={16} className="me-1" />
                      Encerrar Chat
                    </Button>
                  )}
                </div>
              </div>
            </Card.Header>

            <Card.Body 
              className="flex-grow-1 overflow-auto p-3"
              style={{ maxHeight: "400px" }}
            >
              {/* Mostrar mensagens ou estado vazio */}
              {messages.length === 0 ? (
                <div className="text-center py-4">
                  <IconMessage size={32} className="text-muted mb-2" />
                  <p className="text-muted mb-0">
                    {isClientView 
                      ? "Nenhuma mensagem ainda. Envie a primeira mensagem!" 
                      : "Nenhuma mensagem neste chat"
                    }
                  </p>
                </div>
              ) : (
                messages.map(renderMessage)
              )}
              
              {isTyping && typingUser && (
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-light border rounded-3 p-3">
                    <small className="text-muted">
                      {typingUser} está digitando...
                    </small>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </Card.Body>

            {/* Input para enviar mensagens */}
            {selectedChat.status !== "closed" && (selectedChat.status === "active" || 
              (isClientView && selectedChat.status === "waiting") ||
              (!isClientView && [2, 5, 9, 10, 11].includes(user?.plan_id))) && (
              <Card.Footer>
                <div className="d-flex gap-2">
                  <Form.Control
                    as="textarea"
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => {
                      setInputMessage(e.target.value);
                      handleTyping();
                    }}
                    onKeyDown={handleKeyPress}
                    placeholder={
                      selectedChat.status === "waiting" && isClientView
                        ? "Digite sua mensagem... (aguardando atendimento)"
                        : selectedChat.status === "waiting" && [2, 5, 9, 10, 11].includes(user?.plan_id)
                        ? "Digite sua resposta como agente de suporte..."
                        : "Digite sua mensagem..."
                    }
                    disabled={isLoading}
                    rows={1}
                    style={{
                      resize: "none",
                      minHeight: "44px",
                      maxHeight: "120px",
                    }}
                  />
                  
                  <Button
                    variant="primary"
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="px-3"
                  >
                    <IconSend size={16} />
                  </Button>
                </div>
                
                {/* Aviso para cliente quando chat está aguardando */}
                {isClientView && selectedChat.status === "waiting" && (
                  <div className="mt-2">
                    <small className="text-muted">
                      💬 Seu chat foi criado! Aguarde um agente se conectar. Você já pode enviar mensagens.
                    </small>
                  </div>
                )}
              </Card.Footer>
            )}
            
            {/* Aviso quando chat está fechado */}
            {selectedChat.status === "closed" && (
              <Card.Footer>
                <Alert variant="info" className="mb-0">
                  <small>Este chat foi encerrado e não é possível enviar novas mensagens.</small>
                </Alert>
              </Card.Footer>
            )}
          </Card>
        )}
      </Col>

      {/* Modal para Novo Chat */}
      <Modal show={showNewChatModal} onHide={() => setShowNewChatModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isClientView ? "Iniciar Chat de Suporte" : "Novo Chat de Suporte"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Assunto</Form.Label>
              <Form.Control
                type="text"
                value={newChatForm.subject}
                onChange={(e) => setNewChatForm(prev => ({
                  ...prev,
                  subject: e.target.value
                }))}
                placeholder="Descreva brevemente o assunto"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Prioridade</Form.Label>
              <Form.Select
                value={newChatForm.priority}
                onChange={(e) => setNewChatForm(prev => ({
                  ...prev,
                  priority: e.target.value
                }))}
              >
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewChatModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={createNewChat}
            disabled={!newChatForm.subject.trim() || isLoading}
          >
            {isLoading && <Spinner size="sm" className="me-2" />}
            Criar Chat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Finalizar Chat */}
      <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Finalizar Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja finalizar este chat?</p>
          
          <Form.Group className="mb-3">
            <Form.Label>Avaliação (opcional)</Form.Label>
            <div className="d-flex gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <Button
                  key={star}
                  variant={rating >= star ? "warning" : "outline-secondary"}
                  size="sm"
                  onClick={() => setRating(star)}
                >
                  <IconStar size={16} />
                </Button>
              ))}
            </div>
          </Form.Group>
          
          <Form.Group>
            <Form.Label>Comentários (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Deixe um comentário sobre o atendimento"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={closeChatHandler}
            disabled={isLoading}
          >
            {isLoading && <Spinner size="sm" className="me-2" />}
            Finalizar Chat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={
            toastType === "error"
              ? "danger"
              : toastType === "warning"
              ? "warning"
              : toastType === "success"
              ? "success"
              : "primary"
          }
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
      `}</style>
    </Row>
  );
};

export default SupportChat;